import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IFormData, IInput, IResponseForms } from '../../../shared/interfaces';
import api from '../../../services/api';
import {
  ATitle,
  MenuOptions,
  OptionFooter,
  PageIntro,
  PageLayout,
  PagePanel,
  PreviewForm,
} from '../../../components';
import { RiseOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Row } from 'antd';
import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';
import { PermissionActions } from '../../../shared/enums/permissions.enum';

const FormClientQuiz: React.FC = () => {
  const { id } = useParams();
  const { addToast } = useToast();
  const { permissionFind } = useAuth();
  const [title, setTitle] = useState<string>();
  const [clientName, setClientName] = useState<string>();
  const [inputs, setInputs] = useState<IInput[]>([]);
  const [formData, setFormData] = useState<any>({});
  const navigate = useNavigate();

  const loadInputs = useCallback(async () => {
    interface IResponseFormsProps {
      data: IResponseForms;
    }
    const response = await api.get(`/clients-forms/${id}`);
    const { data }: IResponseFormsProps = response;
    setTitle(data.form.title_form);
    setClientName(data.form.client_name);
    setInputs(data.inputs);
  }, []);

  const loadData = useCallback(async () => {
    const response = await api.get(`/forms-data/list/${id}`);
    const { data } = response;
    const objectFormatted = Object.fromEntries(
      data.map((t: IFormData) => [t.input_id, t.value]),
    );
    setFormData(objectFormatted);
  }, [id]);

  const handleSubmit = useCallback(async () => {
    if (!permissionFind([PermissionActions.formsQuiz])) {
      return;
    }

    if (validateInputRequired()) {
      const dataformatted = formattingDataToArray();
      const dataValidated = {
        client_form_id: id,
        data: dataformatted,
      };
      await api
        .post('forms-data', dataValidated)
        .then((response) => {
          if (response.data) {
            addToast({
              type: 'success',
              title: 'Respondido',
              description: 'Respondido com sucesso.',
            });
          }
        })
        .catch((error) => {
          addToast({
            type: 'error',
            title: 'Erro',
            description: error.response.data.message,
          });
        });
    } else {
      addToast({
        type: 'error',
        title: 'Campos obrigatórios',
        description: 'Preencha todos os campos obrigatórios',
      });
    }
  }, [formData, id, addToast, inputs, permissionFind]);

  const validateInputRequired = () => {
    let empty = true;
    inputs.forEach((input) => {
      const obj = formData[input.id ?? ''];
      if ((input.required && !obj) || obj.length === 0) {
        empty = false;
      }
    });
    return empty;
  };

  const formattingDataToArray = (): IFormData[] => {
    const array: IFormData[] = [];
    inputs.forEach((input) => {
      const obj = formData[input.id ?? ''];
      if (obj) {
        if (input.type !== 'checkbox') {
          array.push({
            input_id: input.id ?? '',
            value: formData[input.id ?? ''],
          });
        } else {
          array.push({
            input_id: input.id ?? '',
            value: formData[input.id ?? ''].join(','),
          });
        }
      }
    });
    return array;
  };

  useEffect(() => {
    loadInputs();
    loadData();
  }, [id]);

  return (
    <PageLayout>
      <PageIntro
        badge="Prontuários"
        icon={<RiseOutlined />}
        title={title?.toUpperCase() || 'Prontuário'}
        description="Visualize e preencha o prontuário em uma tela alinhada ao novo padrão visual do sistema."
      />
      <PagePanel>
        <Row
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <ATitle
            level={5}
            icon={
              <Avatar
                size={40}
                style={{ backgroundColor: '#87d068' }}
                icon={<UserOutlined />}
              />
            }
          >
            {clientName}
          </ATitle>
          <MenuOptions
            menu_items={[
              permissionFind([PermissionActions.formsUpdate])
                ? {
                    label: 'Evolução',
                    key: '3',
                    icon: <RiseOutlined />,
                    onClick: () => {
                      navigate(`/form/${id}/evolution`);
                    },
                  }
                : null,
            ]}
          />
        </Row>
        <PreviewForm
          inputs={inputs}
          setInputs={setInputs}
          hideButtonEdit
          formData={formData}
          setFormData={setFormData}
        />
        <OptionFooter
          onClickCancel={() => navigate(-1)}
          onClickOk={() => handleSubmit()}
        />
      </PagePanel>
    </PageLayout>
  );
};

export default FormClientQuiz;
